import { ctxReport } from '@/contexts/ReportCtx';
import { CheckCircleOutlineOutlined, Email, ErrorOutline } from '@mui/icons-material';
import {
  Button,
  Drawer,
  Fab,
  FilledInput,
  FormControl,
  InputAdornment,
  InputLabel,
  Typography,
  FormHelperText,
} from '@mui/material';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotificationStore } from '@/stores/notificationstore';
import { createReport } from '@/generated/client/reports/reports';

export default function ReportEmailButton() {
  const reportCtx = useContext(ctxReport);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const showNotification = useNotificationStore((state) => state.showNotification);

  if (!reportCtx) return;

  async function generateReports() {
    if (
      !reportCtx ||
      !reportCtx.addressee ||
      !reportCtx.selectedDates ||
      !reportCtx.selectedDates.from ||
      !reportCtx.selectedDates.to ||
      (reportCtx.selectedTargets?.length == 0 && reportCtx.selectedDrivers?.length == 0)
    ) {
      return undefined;
    }
    setLoading(true);

    const targetIds = reportCtx.selectedTargets ? reportCtx.selectedTargets.map((target) => target.id) : [];
    const driverIds = reportCtx.selectedDrivers ? reportCtx.selectedDrivers.map((driver) => driver.id) : [];
    const selectedDates = reportCtx.selectedDates;
    const fromTimeMillis = selectedDates.from.getTime();
    const toTimeMillis = selectedDates.to.getTime();
    const request = {
      targetIds: targetIds,
      fromTimeMillis: fromTimeMillis,
      toTimeMillis: toTimeMillis,
      driverIds: driverIds,
      templateIds: reportCtx.selectedTemplateIds,
      addressee: reportCtx.addressee,
    };

    await createReport(request);
    showNotification('info', t('report.successfulReport'), () => {}, 5);
    reportCtx.setSelectedTemplateIds([]);
    setOpen(false);
  }

  function validateEmail(value: string): boolean {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(value);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newValue = e.target.value;
    reportCtx?.setAddressee(newValue);
    setError(newValue !== '' && !validateEmail(newValue));
  }

  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    const newValue = e.target.value;
    reportCtx?.setAddressee(newValue);
    setError(newValue !== '' && !validateEmail(newValue));
  }
  return (
    <>
      <Fab
        variant="extended"
        color="secondary"
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 80,
          right: 16,
          zIndex: 1200,
          boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.3)',
        }}
      >
        <Email sx={{ mr: 1 }} />
        {t('tachograph.downloadButton.send')}
      </Fab>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        slotProps={{
          root: { sx: { zIndex: 1300 } },
          paper: {
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              paddingBottom: '60px',
              overflowY: 'auto',
            },
          },
        }}
      >
        <Typography
          variant="subtitle1"
          gutterBottom
          fontWeight={500}
          sx={{
            padding: '20px',
            borderBottom: '2px solid',
            borderColor: 'divider',
            display: 'inline-block',
            pb: 0.5,
            mb: 1,
          }}
        >
          {t('tachograph.downloadButton.drawerTitle')}
        </Typography>

        <div style={{ padding: '16px 8px 16px 8px' }}>
          <Typography
            variant="subtitle2"
            fontWeight={400}
            sx={{
              padding: '20px',
            }}
          >
            {t('report.sendComment')}
          </Typography>

          <FormControl variant="filled" margin="normal" fullWidth autoFocus error={error}>
            <InputLabel sx={{ color: 'grey' }} htmlFor="email" style={{ color: 'grey' }} shrink={true}>
              {t('tachograph.email.shrinked')}
            </InputLabel>

            <FilledInput
              id="email"
              name="email"
              type="email"
              onChange={handleChange}
              onBlur={handleBlur}
              defaultValue={reportCtx.addressee}
              placeholder={t('tachograph.email.placeHolder')}
              data-qa-element-id="email"
              sx={{
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
                '&.Mui-focused': {
                  backgroundColor: 'transparent',
                },
                // text color
                color: 'black',
                fontStyle: 'italic',

                '&::placeholder': {
                  color: 'grey.500',
                  opacity: 1,
                },

                '&:before, &:after': {
                  borderBottom: '1px solid grey',
                },
                '&:hover:not(.Mui-disabled, .Mui-error):before': {
                  borderBottom: '1px solid black',
                },
              }}
              endAdornment={
                error ? (
                  <InputAdornment position="end" sx={{ color: '#d32f2f' }}>
                    <ErrorOutline fontSize="small" />
                  </InputAdornment>
                ) : (
                  !!reportCtx.addressee && (
                    <InputAdornment position="end" sx={{ color: 'green' }}>
                      <CheckCircleOutlineOutlined fontSize="small" />
                    </InputAdornment>
                  )
                )
              }
            />
            {error && <FormHelperText id="email-error">{t('error.common.badEmail')}</FormHelperText>}
          </FormControl>

          <Button
            variant="contained"
            color="secondary"
            onClick={generateReports}
            disabled={loading || reportCtx.addressee === '' || error}
            fullWidth
            sx={{
              mt: 2,
              width: '60%',
              ml: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              fontSize: '14px',
              padding: '6px 12px',
            }}
          >
            <Email />
            {t('tachograph.downloadButton.send')}
          </Button>
        </div>
      </Drawer>
    </>
  );
}
